import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import httpService from 'services/httpService.js';
import { store } from 'Store';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import ConfirmationModal from 'Components/Common/ConfirmationModal';

import AnnouncementsHeader from './components/AnnouncementsHeader';
import SearchBar from './components/SearchBar';
import FiltersButton from './components/FiltersButton';
import DateRangeFilter from './components/DateRangeFilter';
import AnnouncementCard from './components/AnnouncementCard';
import CreateAnnouncementModal from './components/CreateAnnouncementModal';
import EmptyState from './components/EmptyState';
import EmailHistoryTab from './components/EmailHistoryTab';

import { MdPushPin } from 'react-icons/md';

import styles from './AnnouncementsV2.module.css';

const AnnouncementsV2 = ({ courseId: propCourseId }) => {
  const params = useParams();
  const courseId = propCourseId || params.courseId;

  // State
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [emailRefreshTrigger, setEmailRefreshTrigger] = useState(0);
  const [deleteModal, setDeleteModal] = useState({ show: false, announcement: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Refs
  const timeoutRef = useRef(null);

  // Get user from store
  const getUser = () => {
    const state = store.getState();
    return state.userInfo;
  };

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const url = `/announcements/api/announcement/${courseId}/get_all_announcements/?format=json`;
      const res = await httpService.get(url);
      const data = res.data;

      if (data.isEmpty) {
        setAnnouncements([]);
      } else {
        setAnnouncements(data.all || []);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      displayGlobalMessage('Failed to load announcements', 'error');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  // Initial fetch
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Filter announcements based on search and date range
  const filteredAnnouncements = announcements.filter((announcement) => {
    // Search filter (title and description)
    const searchLower = searchText.toLowerCase().trim();
    const titleMatch = announcement.title?.toLowerCase().includes(searchLower) || false;
    const descriptionMatch = announcement.description?.toLowerCase().includes(searchLower) || false;
    const authorMatch = announcement.created_by
      ? `${announcement.created_by.first_name} ${announcement.created_by.last_name}`.toLowerCase().includes(searchLower)
      : false;

    if (searchLower && !titleMatch && !descriptionMatch && !authorMatch) {
      return false;
    }

    // Date range filter
    if (fromDate) {
      const announcementDate = new Date(announcement.publish_date);
      const filterFromDate = new Date(fromDate);
      if (announcementDate < filterFromDate) {
        return false;
      }
    }

    if (toDate) {
      const announcementDate = new Date(announcement.publish_date);
      const filterToDate = new Date(toDate);
      filterToDate.setHours(23, 59, 59, 999); // End of day
      if (announcementDate > filterToDate) {
        return false;
      }
    }

    return true;
  });

  // Separate pinned and unpinned announcements
  const pinnedAnnouncements = filteredAnnouncements.filter(a => a.pinned);
  const allAnnouncements = filteredAnnouncements.filter(a => !a.pinned);

  // Handlers
  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchText('');
  }, []);

  const handleToggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  const handleFromDateChange = useCallback((value) => {
    setFromDate(value);
  }, []);

  const handleToDateChange = useCallback((value) => {
    setToDate(value);
  }, []);

  const handleClearDates = useCallback(() => {
    setFromDate('');
    setToDate('');
  }, []);

  const handleCreateNew = useCallback(() => {
    setEditingAnnouncement(null);
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
    setEditingAnnouncement(null);
  }, []);

  const handleAnnouncementCreated = useCallback((result) => {
    setShowCreateModal(false);
    setEditingAnnouncement(null);
    fetchAnnouncements();

    // If email task was started, trigger EmailHistoryTab to refresh and show progress
    if (result?.emailTaskId) {
      setEmailRefreshTrigger(prev => prev + 1);
    }
  }, [fetchAnnouncements]);

  const handleEmailTaskComplete = useCallback(() => {
    // Optionally refresh announcements or hide email history
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleEdit = useCallback((announcement) => {
    setEditingAnnouncement(announcement);
    setShowCreateModal(true);
  }, []);

  const handleDelete = useCallback((announcement) => {
    setDeleteModal({ show: true, announcement });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteModal.announcement) return;

    setIsDeleting(true);
    const user = getUser();
    const url = `/announcements/api/announcement/${deleteModal.announcement.id}/delete_announcement/?format=json`;

    try {
      await httpService.instance({
        method: 'delete',
        url: url,
        headers: {
          Authorization: `Token ${user.token}`,
        },
      });
      displayGlobalMessage('Announcement deleted successfully', 'success');
      setDeleteModal({ show: false, announcement: null });
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      displayGlobalMessage('Failed to delete announcement', 'error');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.announcement, fetchAnnouncements]);

  const cancelDelete = useCallback(() => {
    setDeleteModal({ show: false, announcement: null });
  }, []);

  const handlePin = useCallback(async (announcementId, isPinned) => {
    const user = getUser();
    const url = `/announcements/api/announcement/${announcementId}/pinUnpin_announcement/?format=json`;

    try {
      await httpService.instance({
        method: 'patch',
        url: url,
        headers: {
          Authorization: `Token ${user.token}`,
        },
      });
      displayGlobalMessage(isPinned ? 'Announcement unpinned' : 'Announcement pinned', 'success');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error pinning announcement:', error);
      displayGlobalMessage('Failed to update pin status', 'error');
    }
  }, [fetchAnnouncements]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className={styles.announcementsV2}>
      <div className={styles.announcementsContainer}>
        <AnnouncementsHeader onCreateNew={handleCreateNew} />

        <div className={styles.searchFilterSection}>
          <div className={styles.searchRow}>
            <SearchBar
              value={searchText}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
            <FiltersButton
              isActive={showFilters || fromDate || toDate}
              onClick={handleToggleFilters}
            />
          </div>
          {showFilters && (
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={handleFromDateChange}
              onToDateChange={handleToDateChange}
              onClear={handleClearDates}
            />
          )}
        </div>

        {/* Email History Section - shows active email tasks */}
        <EmailHistoryTab
          courseId={courseId}
          onTaskComplete={handleEmailTaskComplete}
          refreshTrigger={emailRefreshTrigger}
        />

        {/* Pinned Announcements Section */}
        {pinnedAnnouncements.length > 0 && (
          <div className={styles.pinnedSection}>
            <div className={styles.sectionHeader}>
              <MdPushPin size={20} className={styles.sectionIcon} />
              Pinned Announcements
            </div>
            <div className={styles.announcementList}>
              {pinnedAnnouncements.map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  onEdit={() => handleEdit(announcement)}
                  onDelete={() => handleDelete(announcement)}
                  onPin={() => handlePin(announcement.id, announcement.pinned)}
                />
              ))}
            </div>
          </div>
        )}

        {/* All Announcements Section */}
        <div className={styles.allSection}>
          {(pinnedAnnouncements.length > 0 || allAnnouncements.length > 0) && (
            <div className={styles.sectionHeader}>
              All Announcements
            </div>
          )}
          <div className={styles.announcementList}>
            {allAnnouncements.length > 0 ? (
              allAnnouncements.map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  onEdit={() => handleEdit(announcement)}
                  onDelete={() => handleDelete(announcement)}
                  onPin={() => handlePin(announcement.id, announcement.pinned)}
                />
              ))
            ) : filteredAnnouncements.length === 0 ? (
              <EmptyState />
            ) : null}
          </div>
        </div>
      </div>

      <CreateAnnouncementModal
        show={showCreateModal}
        onHide={handleCloseModal}
        onSuccess={handleAnnouncementCreated}
        editAnnouncement={editingAnnouncement}
        courseId={courseId}
      />

      <ConfirmationModal
        show={deleteModal.show}
        title="Delete Announcement"
        itemName={deleteModal.announcement?.title}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AnnouncementsV2;
